// NOTE Logique pure, sans dépendance serveur : elle est importée par le composant ReactJS CfcFeeSimulator (navigateur).
// Les taux de frais sont lus depuis les options côté serveur par CfcFeeLogic.getFeeRates.
const CfcFeeSimulationLogic = Object.create(null);

/** @typedef {import('./CfcFeeLogic.js').CfcFeeRatesObj} CfcFeeRatesObj */

/**
 * @typedef {object} CfcFeeDetailObj - Détail d'un frais dans la simulation
 * @property {string} label - Libellé du frais
 * @property {number} percent - Pourcentage du frais par transaction
 * @property {number} euro - Montant fixe du frais par transaction
 * @property {number} amountPerAthlete - Montant de ce frais pour une inscription
 * @property {'price'|'net'} base - Montant sur lequel s'applique le pourcentage : prix payé, ou montant net après les frais de paiement en ligne
 */

/**
 * @typedef {object} CfcFeeSimulationObj - Résultat d'une simulation de frais
 * @property {number} pricePerAthlete - Prix payé par un athlète
 * @property {number} netPerAthlete - Montant net d'une inscription, après les frais de paiement en ligne
 * @property {number} feesPerAthlete - Frais d'une inscription
 * @property {number} revenuePerAthlete - Revenu de l'organisateur pour une inscription (prix - frais)
 * @property {number} totalAmount - Montant total payé par les athlètes
 * @property {number} totalFees - Frais totaux
 * @property {number} totalRevenue - Revenu total de l'organisateur
 * @property {Array<CfcFeeDetailObj>} details - Détail des frais appliqués
 */

CfcFeeSimulationLogic.simulateFees = simulateFees;
CfcFeeSimulationLogic.getPlatformFees = getPlatformFees;
CfcFeeSimulationLogic.getPaymentFees = getPaymentFees;

/**
 * Calcule les frais de la plateforme d'une inscription : pourcentage (du sport ou fixé pour la compétition) appliqué au montant net,
 * c'est-à-dire après les frais de paiement en ligne prélevés par StripeJS, plus le montant du chronométrage si la compétition est chronométrée
 * TU DONE 🗸
 * @param {CfcFeeRatesObj} feeRates - Taux des frais de la compétition
 * @param {number} netAmount - Montant net de l'inscription en euros (prix payé - frais de paiement en ligne)
 * @param {boolean} withChrono - Si la compétition est chronométrée
 * @returns {number}
 */
function getPlatformFees(feeRates, netAmount, withChrono) {
  const amount = Math.max(netAmount || 0, 0);

  return amount ? _round((amount * feeRates.sportPercent) / 100 + (withChrono ? feeRates.chronoEuro : 0)) : 0;
}

/**
 * Simule les frais et les revenus d'une distance, comme lors d'un paiement : les frais de paiement en ligne s'appliquent au prix payé,
 * puis le pourcentage de la plateforme au montant net restant, plus le chronométrage si la compétition est chronométrée ;
 * les frais sont déduits du prix payé par l'athlète
 * TU DONE 🗸
 * @param {CfcFeeRatesObj} feeRates - Taux des frais de la compétition
 * @param {number} price - Prix d'une inscription en euros
 * @param {number} nbRegistrations - Nombre d'inscriptions
 * @param {boolean} withChrono - Si la compétition est chronométrée
 * @returns {CfcFeeSimulationObj}
 */
function simulateFees(feeRates, price, nbRegistrations, withChrono) {
  const pricePerAthlete = Math.max(price || 0, 0);
  const nbAthletes = Math.max(Math.floor(nbRegistrations || 0), 0);
  const paymentFees = getPaymentFees(feeRates, pricePerAthlete);
  const netPerAthlete = Math.max(_round(pricePerAthlete - paymentFees), 0);
  const platformFees = getPlatformFees(feeRates, netPerAthlete, false);
  const chronoFees = withChrono && pricePerAthlete ? feeRates.chronoEuro : 0;
  const details = /** @type {Array<CfcFeeDetailObj>} */ ([
    { label: 'Frais de paiement en ligne', percent: feeRates.paymentPercent, euro: feeRates.paymentEuro, amountPerAthlete: paymentFees, base: 'price' },
    { label: `Frais de la plateforme (${ feeRates.sportType || 'sport non défini' })`, percent: feeRates.sportPercent, euro: 0, amountPerAthlete: platformFees, base: 'net' },
    ...(withChrono ? [{ label: 'Chronométrage', percent: 0, euro: feeRates.chronoEuro, amountPerAthlete: chronoFees, base: 'price' }] : [])
  ]);
  const feesPerAthlete = _round(paymentFees + platformFees + chronoFees);

  return {
    pricePerAthlete,
    netPerAthlete,
    feesPerAthlete,
    revenuePerAthlete: _round(pricePerAthlete - feesPerAthlete),
    totalAmount: _round(pricePerAthlete * nbAthletes),
    totalFees: _round(feesPerAthlete * nbAthletes),
    totalRevenue: _round((pricePerAthlete - feesPerAthlete) * nbAthletes),
    details
  };
}

/**
 * Calcule les frais de paiement en ligne d'une inscription (pourcentage et montant fixe appliqués au prix payé)
 * @param {CfcFeeRatesObj} feeRates - Taux des frais de la compétition
 * @param {number} price - Prix payé par l'athlète en euros
 * @returns {number}
 */
function getPaymentFees(feeRates, price) {
  return price ? _round((price * feeRates.paymentPercent) / 100 + feeRates.paymentEuro) : 0;
}

/* --------------- PRIVATE FUNCTION --------------- */
/**
 * Arrondit un montant au centime
 * @param {number} value - Montant
 * @returns {number}
 * @private
 */
function _round(value) {
  return Math.round(value * 100) / 100;
}
/* --------------- /PRIVATE FUNCTION --------------- */

module.exports = CfcFeeSimulationLogic;
